import type { AgentMessage, AgentMessageFragment, Message } from "@/hooks/use-stream-conversation";
import { useCallback, useMemo, type FC } from "react";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { IconThumbsDown } from "@/assets/icons/geist/IconThumbsDown";
import { IconThumbsUp } from "@/assets/icons/geist/IconThumbsUp";
import { Button } from "@/components/flexkit/Button";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { messageToText } from "@/utils/agent-executions";
import { useSegmentTrack } from "@/hooks/use-segment";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { AgentExecution_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { Tooltip } from "@/components/Tooltip";
import { cn } from "@/components/podkit/lib/cn";
import { UserMsg } from "@/components/flexkit/chat/UserMsg";
import { isUserInputFragmentPending } from "@/utils/chat";
import { Fragment } from "@/components/ai/Fragment";

export const ChatBubble: FC<{
    message: Message;
    onClick?: (message: AgentMessageFragment) => void;
    agentExecution: PlainAgentExecution;
    isLast: boolean;
    isFirst?: boolean;
    agentMessages?: AgentMessage[];
    onSendMessage?: (message: string) => void;
    readOnly?: boolean;
}> = ({ message, onClick, agentExecution, isLast, isFirst, agentMessages, onSendMessage, readOnly }) => {
    const isPending = useMemo(() => {
        if (message.type !== "user") {
            return false;
        }
        // The initial user message is never pending — welcome messages
        // act as the agent's acknowledgment during first environment start.
        if (isFirst) {
            return false;
        }
        return message.fragments.some((fragment) => isUserInputFragmentPending(fragment.id, agentMessages));
    }, [message, isFirst, agentMessages]);

    switch (message.type) {
        case "agent":
            return (
                <ChatBubbleAgent
                    message={message}
                    onClick={onClick}
                    agentExecution={agentExecution}
                    isLast={isLast}
                    onSendMessage={onSendMessage}
                    readOnly={readOnly}
                    data-tracking-id="chat-bubble-agent"
                />
            );
        case "user":
            return <UserMsg message={message} isPending={isPending} agentExecutionId={agentExecution.id} />;
    }
};

const ChatBubbleAgent: FC<{
    message: AgentMessage;
    onClick?: (message: AgentMessageFragment) => void;
    agentExecution: PlainAgentExecution;
    isLast: boolean;
    onSendMessage?: (message: string) => void;
    readOnly?: boolean;
}> = ({ message, onClick, agentExecution, isLast, onSendMessage, readOnly }) => {
    const segmentTrack = useSegmentTrack();
    const [copied, setCopied] = useTemporaryState(false, 2000);
    const [votedUp, setVotedUp] = useTemporaryState(false, 2000);
    const [votedDown, setVotedDown] = useTemporaryState(false, 2000);

    const firstBlockId = message?.fragments?.[0]?.id;
    const lastBlockId = message?.fragments?.[message?.fragments?.length - 1]?.id;

    const handleCopyToClipboard = useCallback(async () => {
        setCopied(true);
        try {
            await navigator.clipboard.writeText(messageToText(message));
        } catch (error) {
            setCopied(false);
            toast({
                title: "Failed to copy to clipboard",
                description: formatError(error),
            });
        }
    }, [setCopied, message]);

    const handleThumbsUp = useCallback(() => {
        setVotedUp(true);
        segmentTrack("agent_message_voted", {
            vote: "up",
            agent_id: agentExecution?.spec?.agentId,
            agent_execution_id: agentExecution.id,
            first_block_id: firstBlockId,
            last_block_id: lastBlockId,
        });
    }, [setVotedUp, segmentTrack, agentExecution?.spec?.agentId, agentExecution.id, firstBlockId, lastBlockId]);

    const handleThumbsDown = useCallback(() => {
        setVotedDown(true);
        segmentTrack("agent_message_voted", {
            vote: "down",
            agent_id: agentExecution?.spec?.agentId,
            agent_execution_id: agentExecution.id,
            first_block_id: firstBlockId,
            last_block_id: lastBlockId,
        });
    }, [setVotedDown, segmentTrack, agentExecution?.spec?.agentId, agentExecution.id, firstBlockId, lastBlockId]);

    const isDone =
        agentExecution.status?.phase === AgentExecution_Phase.WAITING_FOR_INPUT ||
        agentExecution.status?.phase === AgentExecution_Phase.STOPPED;

    // Only mark as naturally completed if the agent stopped on its own (not manually stopped by user)
    // When manually stopped: spec.desired_phase === STOPPED
    // When naturally completed: spec.desired_phase !== STOPPED (usually RUNNING)
    const isNaturallyCompleted = isDone && agentExecution.spec?.desiredPhase !== AgentExecution_Phase.STOPPED;

    const alwaysShow = isDone && isLast;

    return (
        <div className="flex w-full flex-col gap-2 p-0 text-base">
            <div className="group">
                <div className="flex flex-col gap-4">
                    {message.fragments.map((fragment, fragmentIndex) => {
                        const isLastFragment = fragmentIndex === message.fragments.length - 1;
                        const shouldHighlightFragment = isNaturallyCompleted && isLast && isLastFragment;
                        return (
                            <Fragment
                                key={fragment.id}
                                agentExecution={agentExecution}
                                fragment={fragment}
                                shouldHighlightFragment={shouldHighlightFragment}
                                onClick={onClick}
                                onSendMessage={onSendMessage}
                                data-tracking-id="default-tracking-id"
                            />
                        );
                    })}
                </div>

                {!readOnly && (
                    <div
                        className="mt-2 flex min-h-[25px] justify-end duration-700 animate-in fade-in"
                        aria-hidden={!alwaysShow}
                    >
                        <div
                            className={cn(
                                "flex gap-1 rounded-md border-0.5 border-border-base p-0.5 opacity-0 transition-opacity group-hover:opacity-100",
                                { "opacity-100": alwaysShow },
                            )}
                        >
                            <Tooltip content="Give positive feedback">
                                <Button
                                    variant="ghost"
                                    className={cn("size-5 border-0", { "disabled:text-green-500": votedUp })}
                                    size={"xs"}
                                    LeadingIcon={votedUp ? IconCheck : IconThumbsUp}
                                    onClick={handleThumbsUp}
                                    disabled={votedUp}
                                    aria-label="Thumbs up"
                                    tabIndex={alwaysShow ? undefined : -1}
                                    data-tracking-id="thumbs-up-chat-bubble"
                                />
                            </Tooltip>
                            <Tooltip content="Give negative feedback">
                                <Button
                                    variant="ghost"
                                    className={cn("size-5 border-0", { "disabled:text-green-500": votedDown })}
                                    size={"xs"}
                                    LeadingIcon={votedDown ? IconCheck : IconThumbsDown}
                                    onClick={handleThumbsDown}
                                    disabled={votedDown}
                                    aria-label="Thumbs down"
                                    tabIndex={alwaysShow ? undefined : -1}
                                    data-tracking-id="thumbs-down-chat-bubble"
                                />
                            </Tooltip>
                            <Tooltip content="Copy to clipboard">
                                <Button
                                    variant="ghost"
                                    disabled={copied}
                                    className={cn("size-5 border-0", { "disabled:text-green-500": copied })}
                                    size={"xs"}
                                    LeadingIcon={copied ? IconCheck : IconCopy}
                                    onClick={handleCopyToClipboard}
                                    aria-label="Copy to clipboard"
                                    tabIndex={alwaysShow ? undefined : -1}
                                    data-tracking-id="copy-to-clipboard-chat-bubble"
                                />
                            </Tooltip>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
